import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { ANIMATION_TIMING, EASING_CURVES } from "../utils/motion";
import Round from "../Round.svg";


const VideoGallery = ({ videosProps }) => {
  const videoRefs = useRef({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadedCount, setLoadedCount] = useState(0);
  const totalVideosRef = useRef(0);
  const loadedVideosRef = useRef(new Set());

  // Utility function to shuffle array in place
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Count total videos to load
  const countTotalVideos = useCallback((data) => {
    return data.reduce((total, column) => total + column.videos.length, 0);
  }, []);

  // Reset loading state
  const resetLoadingState = useCallback(() => {
    setIsLoading(true);
    setLoadedCount(0);
    loadedVideosRef.current = new Set();
  }, []);

  // Handle individual video load
  const handleVideoLoad = useCallback((videoId, videoElement) => {
    console.log("Video loaded:", videoId);

    if (!loadedVideosRef.current.has(videoId)) {
      loadedVideosRef.current.add(videoId);
      setLoadedCount((prev) => {
        const newCount = prev + 1;
        // Check if all videos are loaded
        if (newCount >= totalVideosRef.current) {
          setIsLoading(false);
        }
        return newCount;
      });
    }

    // Ensure video is muted and plays inline
    if (videoElement) {
      videoElement.muted = true;
      videoElement.playsInline = true;
    }
  }, []);

  // Handle video load error
  const handleVideoError = useCallback((videoId) => {
    console.log("Video failed to load:", videoId);

    if (!loadedVideosRef.current.has(videoId)) {
      loadedVideosRef.current.add(videoId);
      setLoadedCount((prev) => {
        const newCount = prev + 1;
        // Check if all videos are loaded
        if (newCount >= totalVideosRef.current) {
          setIsLoading(false);
        }
        return newCount;
      });
    }
  }, []);

  const baseVideoAssets = videosProps;

  // Function to generate random video data
  const generateRandomVideoData = useCallback((baseVideoAssets, shuffleArray) => {
    // Group assets by aspect ratio
    const assetsByRatio = {
      '16:9': [],
      '4:5': [],
      '9:16': []
    };

    baseVideoAssets.forEach(asset => {
      if (assetsByRatio[asset.aspectRatio]) {
        assetsByRatio[asset.aspectRatio].push(asset);
      }
    });

    // Shuffle each group
    Object.keys(assetsByRatio).forEach(ratio => {
      assetsByRatio[ratio] = shuffleArray(assetsByRatio[ratio]);
    });

    const length = window.innerWidth <= 768 ? 4 : 5;
    return Array.from({ length }, (_, columnIndex) => {
      // Shuffle the aspect ratios for random order in each column
      const shuffledRatios = shuffleArray(['16:9', '4:5', '9:16']);
      // Create three videos for this column, one of each aspect ratio in random order
      const videos = shuffledRatios.map((ratio, videoIndex) => {
        const group = assetsByRatio[ratio];
        // Cycle through the shuffled group if needed
        const assetIndex = columnIndex % group.length;
        const asset = group[assetIndex];

        return {
          id: `${columnIndex + 1}-${videoIndex + 1}`,
          url: asset?.url,
          aspectRatio: asset.aspectRatio,
        };
      });

      return {
        id: columnIndex + 1,
        videos,
      };
    });
  }, []);

  // State variable for videoData
  const [videoData, setVideoData] = useState(() => {
    const initialData = generateRandomVideoData(baseVideoAssets, shuffleArray);
    totalVideosRef.current = countTotalVideos(initialData);
    return initialData;
  });

  // Update total videos when videoData changes
  useEffect(() => {
    const newTotal = countTotalVideos(videoData);
    totalVideosRef.current = newTotal;

    // Add a timeout to ensure loading completes even if some videos don't load
    const loadingTimeout = setTimeout(() => {
      if (loadedVideosRef.current.size < newTotal) {
        setLoadedCount(newTotal);
        setIsLoading(false);
      }
    }, 3000); // 3 second timeout for slower connections

    return () => {
      clearTimeout(loadingTimeout);
    };
  }, [videoData, countTotalVideos]);

  // Function to reload gallery with new random video data
  const reloadGallery = () => {
    resetLoadingState();
    // Force re-render with loading state before generating new data
    setTimeout(() => {
      const newVideoData = generateRandomVideoData(
        baseVideoAssets,
        shuffleArray
      );
      totalVideosRef.current = countTotalVideos(newVideoData);
      setVideoData(newVideoData);
    }, 100);
  };

  const handleMouseEnter = (videoElement) => {
    if (videoElement) {
      videoElement.muted = true; // Ensure no audio
      videoElement.play().catch((e) => console.log("Video play failed:", e));
    } else {
      console.log("Video element not found");
    }
  };

  const handleMouseLeave = (videoElement) => {
    if (videoElement) {
      videoElement.pause();
      videoElement.currentTime = 0;
    }
  };

  const getAspectRatioClass = (aspectRatio) => {
    if (aspectRatio?.includes("16:9")) {
      return "aspect-video"
    } else if (aspectRatio?.includes("4:5")) {
      return "aspect-[4/5]";
    } else if (aspectRatio?.includes("9:16")) {
      return "aspect-[9/16]";
    }
    return "aspect-video";
  };

  return (
    <div className="py-16 px-6 pt-48">
      <div className="max-w-[1400px] mx-auto">
        <motion.h1
          className="projects-title text-7xl sm:text-7xl md:text-6xl lg:text-6xl xl:text-7xl text-left"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: ANIMATION_TIMING.durations.normal,
            ease: EASING_CURVES.smooth,
          }}
        >
          créations
        </motion.h1>
        {/* Reload button */}
        <div className="mb-8 text-center items-center justify-center flex">
          <motion.button
            className="flex p-2 color-primary bg-black text-primary font-lexend shadow-lg mb-5 font-bold text-xs sm:text-md md:text-lg lg:text-lg "
            style={{
              scale: 2,
              zIndex: 40,
            }}
            onClick={reloadGallery}
            whileHover={{ scale: 2.05 }}
            whileTap={{ scale: 1.95 }}
            transition={{
              duration: ANIMATION_TIMING.durations.normal,
              ease: EASING_CURVES.smooth,
            }}
          >
            {" "}
            Recharger la galerie
          </motion.button>
        </div>

        {/* Loading indicator and grid - controlled by opacity */}
        <div className="relative">




          {isLoading === true && (
            <motion.div
              className="relative flex flex-col items-center justify-center h-screen"
              animate={{ opacity: isLoading < 100 ? 1 : 0.1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.5,
                ease: EASING_CURVES.smooth,
              }}
            >
              <motion.img
                src={Round}
                className="w-[200px]"
                animate={{ rotate: 360 }}
                transition={{
                  repeat: Infinity,
                  duration: 6,
                  ease: EASING_CURVES.smooth,
                }}
                alt="logo-round"
              />
            </motion.div>
          )}

          <motion.div
            className="grid gap-6 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: isLoading ? 0 : 1 }}
            transition={{
              duration: ANIMATION_TIMING.durations.fast,
              delay: 0,
              ease: EASING_CURVES.smooth,
            }}
          >
            {videoData.map((column, columnIndex) => (
              <motion.div
                key={column.id}
                className="flex flex-col gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: ANIMATION_TIMING.durations.normal,
                  delay: 0.2 * columnIndex,
                  ease: EASING_CURVES.smooth,
                }}
              >
                {column.videos.map((video, videoIndex) => {
                  return (
                    <motion.div
                      key={video.id}
                      className={`relative overflow-hidden bg-gray-100 ${getAspectRatioClass(
                        video.aspectRatio
                      )}`}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2, ease: EASING_CURVES.smooth }}
                      onMouseEnter={() => {
                        handleMouseEnter(videoRefs.current[video.id]);
                      }}
                      onMouseLeave={() => {
                        handleMouseLeave(videoRefs.current[video.id]);
                      }}
                      style={{ pointerEvents: 'auto' }}
                    >


                      {/* Video */}
                      <motion.video
                        ref={(el) => {
                          videoRefs.current[video.id] = el;
                        }}
                        src={video.url}
                        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                        loop
                        muted
                        playsInline
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3, ease: EASING_CURVES.smooth }}
                        onLoad={() => handleVideoLoad(video.id, videoRefs.current[video.id])}
                        onError={() => handleVideoError(video.id)}
                      />

                      {/* Overlay for smooth transition */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300" />

                      {/* Aspect ratio indicator */}
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                        {video.aspectRatio}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ))}
          </motion.div>



          {/* Progress indicator */}
          {/* {totalThumbnailsRef.current > 0 && (
              <motion.p
                className="text-sm text-gray-500 mt-6 font-lexend"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {loadedCount} / {totalThumbnailsRef.current} images chargées
              </motion.p>
            )} */}
        </div>
      </div>
    </div>
  );
};

export default VideoGallery;
